import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    MdsDropdownComponent,
    MdsIconButtonComponent,
    MdsTextareaComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { Expense } from '@models/request';

import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { CurrencyViewModel } from '@models/currency';
import { StudyExpense } from '@models/study';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { isTypeMileageExpense, MdsOptionGeneric } from '@utility/utility';
import moment from 'moment';
import { combineLatest, delay, filter, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { MileageExpenseEditComponent } from '../mileage-expense-edit/mileage-expense-edit.component';
import { OtherExpenseEditComponent } from '../other-expense-edit/other-expense-edit.component';
import { ExpenseFormGroup } from './expense-edit.form';

@Component({
    selector: 'medpace-expense-edit',
    imports: [
        MdsDropdownComponent,
        MileageExpenseEditComponent,
        OtherExpenseEditComponent,
        AsyncPipe,
        MdsTextareaComponent,
        MdsIconButtonComponent,
    ],
    templateUrl: './expense-edit.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpenseEditComponent {
    index = input.required<number>();
    formGrp = input.required<ExpenseFormGroup>();
    formGrp$ = toObservable(this.formGrp);
    expense = input<Expense | undefined>();
    expenseMaybe$ = toObservable(this.expense);
    expenseNotNull$ = this.expenseMaybe$.pipe(filter(Boolean));
    requestService = inject(ReimbursementRequestService);
    patientStateService = inject(PatientStateService);
    currencyStateService = inject(CurrencyStateService);
    userService = inject(UserServiceV2);
    removeEvent = output<number>();
    studyStateService = inject(StudyStateService);

    elevatedAccess$ = this.userService.permissions.request.access.elevated$;
    //dynamic list of expense types per expense
    // will always contain currently selected study expenses for the study
    // plus whatever was previously chosen for the existing expense (if it exists)
    // this will take into account that the study expenses list in the study may have been modified
    // after the expense has been created and it's expense type is no longer there - then it dynamically adds it
    availableExpensesOptions$ = combineLatest({
        studyActiveExpensesAsMdsOptions: this.studyStateService.studyActiveExpensesAsMdsOptions$,
        expense: this.expenseMaybe$,
    }).pipe(
        switchMap(({ expense, studyActiveExpensesAsMdsOptions }) => {
            // creating new expense - all selected study expense types
            if (!expense) return of(studyActiveExpensesAsMdsOptions);

            // expense exists but its expense type is still in the selected study expenses list
            const existingExpenseTypeIsStillAvailableInStudy = studyActiveExpensesAsMdsOptions.some(
                (expenseType) => expenseType.value.expenseId === expense.expenseId
            );
            if (existingExpenseTypeIsStillAvailableInStudy) return of(studyActiveExpensesAsMdsOptions);
            else {
                return this.studyStateService.getAvailableExpenses().pipe(
                    map((allExpenseTypes) => {
                        // this expense's study expense type taken from all available expenses
                        const existingExpenseType = allExpenseTypes.find(
                            (expenseType) => expenseType.expenseId === expense.expenseId
                        );
                        if (!existingExpenseType)
                            throw new Error(`Expense type with id ${expense.expenseId} could not be found`);

                        // convert to mds option
                        const existingExpenseTypeAsMdsOption: MdsOptionGeneric<StudyExpense> = {
                            value: existingExpenseType,
                            viewValue: existingExpenseType.expenseDefaultValue,
                        };
                        // add the existing expense type on top of study's selected ones
                        return [existingExpenseTypeAsMdsOption, ...studyActiveExpensesAsMdsOptions];
                    })
                );
            }
        }),
        shareReplay(1)
    );
    mileageExpenseComponent = viewChild(MileageExpenseEditComponent);
    otherExpenseComponent = viewChild(OtherExpenseEditComponent);

    readonly activeExpenseComponent = computed(() => this.mileageExpenseComponent() ?? this.otherExpenseComponent());

    /** Exposes live chip amounts (usd, patientsCurrency) from the underlying expense sub-component. */
    readonly chipAmounts = computed(() => this.activeExpenseComponent()?.chipAmountsSignal() ?? null);

    push(): Expense {
        const value = this.formGrp().getRawValue();
        let expense = this.expense();
        if (!expense) expense = new Expense({ id: 0 });

        expense.expenseId = value.type.expenseId;
        expense.attachment = value.file;
        if (this.formGrp().controls.file.dirty) {
            // user changed file
            if (value.file) {
                // new file added
                expense.attachmentId = 0;
            } else {
                expense.attachmentId = null;
            }
        }
        // else: leave expense.attachmentId unchanged

        expense.timestamp = value.receiptDate ? moment(value.receiptDate).format('YYYY-MM-DDTHH:mm:ss') : null;
        expense.currency = value.currency.isocode;
        expense.notes = value.notes;
        if (isTypeMileageExpense(value.type.expenseId)) expense = this.mileageExpenseComponent().push(expense);
        else expense = this.otherExpenseComponent().push(expense);

        return expense;
    }

    expenseType$ = this.formGrp$.pipe(switchMap((formGrp) => formGrp.controls.type.valueChangesReplay));

    isMileageExpense$ = this.expenseType$.pipe(
        map((expenseType) => {
            if (!expenseType) return null;
            return isTypeMileageExpense(expenseType.expenseId);
        }),
        shareReplay(1)
    );
    switchExpenseType$ = this.isMileageExpense$
        .pipe(
            filter((isMileageExpense) => isMileageExpense !== null),
            tap((isMileageExpense) => {
                if (isMileageExpense) {
                    this.formGrp().controls.mileageExpense.enable({ emitEvent: true, force: true });
                    this.formGrp().controls.otherExpense.disable({ emitEvent: true, persistent: true });
                } else {
                    this.formGrp().controls.mileageExpense.disable({ emitEvent: true, persistent: true });
                    this.formGrp().controls.otherExpense.enable({ emitEvent: true, force: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    patchValue$ = combineLatest({
        expense: this.expenseNotNull$,
        availableExpenses: this.availableExpensesOptions$,
        currencyOptions: this.currencyStateService.getCurrenciesAsMdsOptions(),
    })
        .pipe(
            delay(0), // needed to avoid patchValue triggering before form controls are fully initialized - fixes missing options in mds dropdown
            tap(({ availableExpenses, currencyOptions, expense }) => {
                this.formGrp().patchValue(
                    {
                        type:
                            availableExpenses.find((studyExpense) => studyExpense.value.expenseId === expense.expenseId)
                                ?.value ?? null,
                        receiptDate: expense.timestamp ? new Date(expense.timestamp) : null,
                        currency:
                            currencyOptions.find(
                                (currency) => currency.value.isocode.toLowerCase() === expense.currency.toLowerCase()
                            )?.value ?? null,
                        notes: expense.notes,
                    },
                    { emitEvent: true } // must emit to dispplay receiptDate in datepicker
                );
            }),
            takeUntilDestroyed()
        )
        .subscribe();
    initializePreferredCurrencyEffect$ = combineLatest({
        expense: this.expenseMaybe$,
        patient: this.patientStateService.getPatient(),
        currencies: this.currencyStateService.getCurrencies(),
        currencyUSD: this.currencyStateService.getCurrencyUSD(),
        formGrp: this.formGrp$,
    })
        .pipe(
            filter(({ expense }) => !expense), // expense hasnt emitted yet
            tap(({ currencies, currencyUSD, patient, formGrp }) => {
                let currency: CurrencyViewModel | null = null;
                if (patient?.preferredCurrency) {
                    currency = currencies.find((currency) => currency.isocode === patient.preferredCurrency);
                }
                if (!currency) currency = currencyUSD;

                if (currency) formGrp.controls.currency.setValue(currency);
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    removeExpense() {
        this.removeEvent.emit(this.index());
    }
}
